import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import apis from "../../services";

const LotManagementPanel = () => {
  const [lots, setLots] = useState([]);
  const [reconciliation, setReconciliation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lotsResponse, reconciliationResponse] = await Promise.all([
        apis.getShareLots(),
        apis.getShareLotReconciliation().catch((error) => error?.response),
      ]);
      setLots(lotsResponse?.data?.lots || []);
      setReconciliation(reconciliationResponse?.data?.reconciliation || null);
    } catch (error) {
      toast.error(error?.message || "Failed to load share lots");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const expireReservations = async () => {
    setActing(true);
    try {
      const response = await apis.expireShareReservations(250);
      toast.success(`${response?.data?.expiredCount || 0} expired reservations released`);
      await load();
    } catch (error) {
      toast.error(error?.message || "Failed to release expired reservations");
    } finally {
      setActing(false);
    }
  };

  return (
    <Card className="mb-5" style={{ borderRadius: 20 }}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div>
            <h4 className="mb-1">Lot inventory management</h4>
            <small className="text-muted">Live 100,000-share inventory, pricing, reservations and reconciliation.</small>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={load} disabled={loading || acting}>Refresh</Button>
            <Button variant="outline-danger" onClick={expireReservations} disabled={loading || acting}>Release expired reservations</Button>
          </div>
        </div>

        {reconciliation && (
          <Alert variant={reconciliation.reconciled ? "success" : "danger"}>
            <strong>{reconciliation.reconciled ? "Ledger reconciled" : "Reconciliation required"}</strong>
            <div>
              Transactions: {Number(reconciliation.transactionShares || 0).toLocaleString()} shares ·
              Lot ledger: {Number(reconciliation.finalizedWholeShares || 0).toLocaleString()} whole shares
            </div>
            {!reconciliation.reconciled && (reconciliation.issues || []).map((issue) => <div key={issue}>• {issue}</div>)}
          </Alert>
        )}

        <Table responsive hover bordered size="sm">
          <thead><tr><th>Lot</th><th>Status</th><th>Approved price</th><th>Available</th><th>Reserved</th><th>Sold</th><th>Legacy occupied</th><th>Next folio</th><th>Series / class</th></tr></thead>
          <tbody>
            {lots.map((lot) => (
              <tr key={lot._id}>
                <td><strong>{lot.code}</strong></td>
                <td><Badge bg={lot.status === "open" ? "success" : lot.status === "closed" ? "secondary" : "warning"}>{lot.status}</Badge></td>
                <td>${Number(lot.approvedPrice || 0).toFixed(2)} MXN</td>
                <td>{Number(lot.availableShares || 0).toLocaleString()}</td>
                <td>{Number(lot.reservedShares || 0).toLocaleString()}</td>
                <td>{Number(lot.soldShares || 0).toLocaleString()}</td>
                <td>{Number(lot.legacyWholeConsumed || 0).toLocaleString()} + {Number(lot.legacyBackingReserved || 0).toLocaleString()} backing</td>
                <td>{lot.nextShareNumber <= lot.capacity ? `${lot.code}-${String(lot.nextShareNumber).padStart(6, "0")}` : "Complete"}</td>
                <td>{lot.legalSeries} / {lot.legalClass}</td>
              </tr>
            ))}
            {!loading && lots.length === 0 && <tr><td colSpan={9} className="text-center">No lots found</td></tr>}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

export default LotManagementPanel;
